import React from 'react';
import {
  SOURCE_COLORS,
  INVOICE_COLORS,
  INVOICE_LABELS,
  DELIVERY_COLORS,
  DELIVERY_LABELS,
  ROLE_COLORS,
  ROLE_LABELS,
  kaynakLabel,
  ORDER_SOURCES,
  DAVET_DURUMU_LABELS,
  DAVET_DURUMU_COLORS,
} from '../../constants';
import type { DavetDurumu } from '../../constants';
import type { RoleType } from '../../shared/constants/roles';
import type { DeliveryStatusType, ProductType } from '../../shared/constants/statuses';

interface BadgeProps {
  className?: string;
}

// ── Ortak badge stilleri — tüm badge'ler bu base'lerden türer ──
// TEXT_BADGE: ikonsuz badge (SourceBadge, InvoiceBadge vb.)
// ICON_BADGE: ikonlu badge (OrderPaymentBadge, OrderDeliveryBadge vb.)
export const BADGE_BASE = {
  text: 'inline-block text-center px-2 py-1 rounded-full text-xs font-medium',
  icon: 'inline-flex items-center justify-center gap-1 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap',
} as const;

// ── Grup genişlikleri (en uzun label baz) ──
export const BADGE_WIDTHS = {
  source:       'w-[96px]',   // "HB-TrioCare"
  invoice:      'w-[96px]',   // "Kısmi İade" (KISMI_IADE için genişletildi, önce 84px idi)
  delivery:     'w-[88px]',   // "Teslim Edildi"
  role:         'w-[88px]',   // "Görüntüleme"
  orderPayment: 'w-[82px]',   // "Ödenmedi" + ikon
  orderDelivery:'w-[105px]',  // "Kargo Bekleniyor" + ikon
  productType:  'w-[88px]',   // "YEDEK PARÇA"
  userStatus:   'w-[136px]',  // "Davet süresi doldu" (Durum sütunu davet durumunu da gösterir)
  custType:     'w-[116px]',  // "Potansiyel Müşteri"
  custSource:   'w-[88px]',   // "HepsiBurada"
  leadStatus:   'w-[104px]',  // "İletişime Geçildi"
  teslimde:     'w-[68px]',   // "Teslimde"
} as const;

// Dahili kısa referanslar
const BASE = BADGE_BASE.text;
const W = BADGE_WIDTHS;

// Kaynak Badge
interface SourceBadgeProps extends BadgeProps {
  source: keyof typeof SOURCE_COLORS;
}

export const SourceBadge: React.FC<SourceBadgeProps> = ({ source, className = '' }) => {
  const colorClass = SOURCE_COLORS[source] || SOURCE_COLORS[ORDER_SOURCES.HARICI];
  return (
    <span className={`${BASE} ${W.source} ${colorClass} ${className}`}>
      {kaynakLabel(source)}
    </span>
  );
};

// Fatura Durumu Badge
interface InvoiceBadgeProps extends BadgeProps {
  status: keyof typeof INVOICE_COLORS;
}

export const InvoiceBadge: React.FC<InvoiceBadgeProps> = ({ status, className = '' }) => {
  const colorClass = INVOICE_COLORS[status] || INVOICE_COLORS.KESILECEK;
  return (
    <span className={`${BASE} ${W.invoice} ${colorClass} ${className}`}>
      {INVOICE_LABELS[status] || status}
    </span>
  );
};

// Teslimat Durumu Badge
interface DeliveryBadgeProps extends BadgeProps {
  status: DeliveryStatusType;
}

export const DeliveryBadge: React.FC<DeliveryBadgeProps> = ({ status, className = '' }) => {
  const colorClass = DELIVERY_COLORS[status] || DELIVERY_COLORS.HAYIR;
  const label = DELIVERY_LABELS[status] || status;
  return (
    <span className={`${BASE} ${W.delivery} ${colorClass} ${className}`}>
      {label}
    </span>
  );
};

// Kullanıcı Rol Badge
interface RoleBadgeProps extends BadgeProps {
  role: string;
}

export const RoleBadge: React.FC<RoleBadgeProps> = ({ role, className = '' }) => {
  const roleKey = role as RoleType;
  const colorClass = ROLE_COLORS[roleKey] || ROLE_COLORS.goruntuleme;

  return (
    <span className={`${BASE} ${W.role} ${colorClass} ${className}`}>
      {ROLE_LABELS[roleKey] || role}
    </span>
  );
};

// İmalat No Badge
interface ImalatBadgeProps extends BadgeProps {
  imalatNo: number | string;
}

export const ImalatBadge: React.FC<ImalatBadgeProps> = ({ imalatNo, className = '' }) => {
  return (
    <span className={`px-2 py-1 rounded bg-indigo-100 text-indigo-800 text-xs font-mono ${className}`}>
      #{imalatNo}
    </span>
  );
};

// Ürün Tipi Badge
interface ProductTypeBadgeProps extends BadgeProps {
  type: ProductType;
}

const PRODUCT_TYPE_BASE = 'inline-block w-[88px] text-center py-1 rounded-full text-xs font-medium';

export const ProductTypeBadge: React.FC<ProductTypeBadgeProps> = ({ type, className = '' }) => {
  if (type === 'bundle') {
    return <span className={`${PRODUCT_TYPE_BASE} bg-amber-100 text-amber-800 ${className}`}>PAKET</span>;
  }
  if (type === 'yedek_parca') {
    return <span className={`${PRODUCT_TYPE_BASE} bg-blue-100 text-blue-800 ${className}`}>YEDEK PARÇA</span>;
  }
  if (type === 'servis') {
    return <span className={`${PRODUCT_TYPE_BASE} bg-gray-100 text-gray-800 ${className}`}>SERVİS</span>;
  }
  return null;
};

// Kullanıcı Aktif/Pasif Durum Badge
// Genişlik caller'a bırakıldı: desktop sabit (W.userStatus), mobil kartta serbest.
interface UserStatusBadgeProps extends BadgeProps {
  active: boolean;
}

export const UserStatusBadge: React.FC<UserStatusBadgeProps> = ({ active, className = '' }) => {
  const colorClass = active
    ? 'text-green-700 font-bold bg-green-100 border border-green-200'
    : 'text-gray-500 font-bold bg-gray-200 border border-gray-300';
  return (
    <span className={`${BASE} ${colorClass} ${className}`}>
      {active ? 'Aktif' : 'Pasif'}
    </span>
  );
};

// Davet durumu rozeti (durum `components/users/davetDurumu`'ndan). Kullanıcı listesinin
// Durum sütununda Aktif/Pasif rozetinin yerine geçer; genişlik caller'a bırakıldı
// (desktop W.userStatus, mobil kartta serbest).
interface DavetBadgeProps extends BadgeProps {
  durum: DavetDurumu;
}

export const DavetBadge: React.FC<DavetBadgeProps> = ({ durum, className = '' }) => (
  <span className={`${BASE} whitespace-nowrap ${DAVET_DURUMU_COLORS[durum]} ${className}`}>
    {DAVET_DURUMU_LABELS[durum]}
  </span>
);

// Hesap Kilidi Badge (mobil kartta kullanılır; desktop'ta 🔒 emoji ile gösterilir)
export const LockedBadge: React.FC<BadgeProps> = ({ className = '' }) => (
  <span className={`inline-block bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${className}`}>
    Kilitli
  </span>
);
